<template>
  <section class="mx-3">
    <h1>Inventory Calculations</h1>
    <b-row class="mb-3">
      <b-col>
        <b-form @submit="searchForItem">
          <b-form-row class="mb-2">
            <b-col sm>
              <label class="sr-only" for="partNum">Part Number</label>
              <b-form-input id="partNum" placeholder="Part Number" v-model="search.partNum" />
            </b-col>
            <b-col sm>
              <label class="sr-only" for="partDesc">Part Description</label>
              <b-form-input id="partDesc" placeholder="Part Description" v-model="search.partDesc" />
            </b-col>
          </b-form-row>
          <b-form-row>
            <b-col sm>
              <label class="sr-only" for="productNum">Product Number</label>
              <b-form-input id="productNum" placeholder="Product Number" v-model="search.productNum" />
            </b-col>
            <b-col sm>
              <label class="sr-only" for="productDescription">Product Description</label>
              <b-form-input id="productDescription" placeholder="Product Description" v-model="search.productDescription" />
            </b-col>
            <b-col cols="2">
              <label class="sr-only" for="itemType">Item Type</label>
              <b-form-select id="itemType" placeholder="Item Type" v-model="search.itemType" :options="['', 'IND', 'BOM', 'KIT']" />
            </b-col>
          </b-form-row>
          <b-form-row>
            <b-button type="submit" class="mt-2 mx-1" variant="primary">
              Search
            </b-button>
            <b-btn variant="secondary" class="mt-2 mx-1" @click="searchForErrors">
              Show All Errors
            </b-btn>
            <b-btn size="sm" variant="link" @click="showInfoModal('errors')" class="px-0">
              <i class="fas fa-question-circle"></i>
            </b-btn>
          </b-form-row>
        </b-form>
      </b-col>
    </b-row>

    <b-row v-if="fishionInventoryCalcs && fishionInventoryCalcs.length > 0" @row-clicked="grabDescriptions">
      <b-table striped class="fixed-table" :items="fishionInventoryCalcs" :fields="columns" :sort-by="'part_num'">
        <template slot="part_num" slot-scope="row">
          <div class="strong">
            {{ getItemNum(row.item) }}
            <span v-if="row.item.product_num == '0'" class="strong" style="color:red;">
              (NO PRODUCT)
            </span>
          </div>
          <div class="em" style="margin: 0.25em 0 0 0.5em;">
            {{ row.item.item_type.toUpperCase() }} / {{ row.item.custom_parttype }}
          </div>
        </template>
        <template slot="description" slot-scope="row">
          {{ Number(row.item.part_desc) != 0 ? row.item.part_desc : row.item.product_desc }}
        </template>
        <template slot="web_qty" slot-scope="row">
          <div :style="getQtyColor(row.item.web_qty)">
            <strong>{{ row.item.web_qty }}</strong>
          </div>
          <div v-if="row.item.product_num == '0'" class="strong small">
            This item will not post to web
          </div>
          <div class="em small" v-if="row.item.updated_to_web > 0">
            {{ row.item.last_post }}
          </div>
        </template>
        <template slot="purchaseOrders" slot-scope="row">
          <div style="width: 12em;" v-for="order_po in row.item.on_order_po" :key=order_po.num>
            {{ order_po.num }}-({{ order_po.total }})-{{ order_po.scheduledate }}
          </div>
          <div class="em small" v-if="!row.item.receive_date.startsWith('2010-01-01')">
            Date In: <nice-date :value="row.item.receive_date" :show-time="false" is-utc />
          </div>
          <div class="em small" v-if="row.item.receive_date_replacement">
            Verbage: {{ row.item.receive_date_replacement }}
          </div>
          <div class="em small" v-if="row.item.availability_text != 'Do not post' && row.item.availability_text">
            Avail: {{ row.item.availability_text }}
          </div>
          <div class="em small" v-if="row.item.customer_note">
            Note: {{ row.item.customer_note }}
          </div>
          <div class="em small" v-if="!!row.item.on_order_po && row.item.on_order_po.includes('*')">
            (*) indicates a line item that is using the PO's "schedule date" (instead of the PO Item's), due to the PO Item being partially received on this PO
          </div>
        </template>
        <template slot="adjustments" slot-scope="row">
          <div v-if="row.item.adj_override !== 0" class="strong" style="color: red;">
            Override: {{ row.item.adj_override }}
          </div>
          <div v-if="row.item.qty_ceiling != 0" class="string" style="color: red;">
            Qty Ceiling: {{ row.item.qty_ceiling }}
          </div>
          <div v-if="row.item.adj_calc_inventory !== 0">
            Calculation Adjustment: {{ row.item.adj_calc_inventory }}
          </div>
          <div v-if="row.item.adj_web_inventory !== 0">
            Web Adjustment: {{ row.item.adj_web_inventory }}
          </div>

          <div class="em small">
            {{ row.item.custom_field_four }}
          </div>
        </template>
        <template slot="actions" slot-scope="row">
          <b-button size="sm" @click="row.toggleDetails(); grabDescriptions(row.item);">
            {{ row.detailsShowing ? "Hide" : "Show" }} Details
          </b-button>
        </template>
        <template slot="row-details" slot-scope="row">
          <b-row class="text-center" v-if="row.item.updated_to_web">
            <b-col class="ml-auto my-1">
              <b-btn :href="'https://www.andymark.com/' + row.item.product_num" target="_blank" variant="outline-info" size="sm">
                Show Item on Website
              </b-btn>
            </b-col>
          </b-row>
          <b-row class="text-center" v-else>
            <b-col class="ml-auto my-1">
              <strong>This item's quantity is not being updated to the website!</strong>
            </b-col>
          </b-row>
          <fishbowl-data :item="row.item" @showModal="showInfoModal" />
          <calculated-data :item="row.item" @showModal="showInfoModal" />
          <lowest-parts-data :item="row.item" :descriptions="partDescriptions" :uoms="partUOMs" @showModal="showInfoModal"/>
        </template>
      </b-table>
    </b-row>

    <b-modal ref="infoModalRef" centered scrollable hide-footer size="lg" title="More Info">
      <div v-html="infoModalText" />
    </b-modal>

    <b-modal ref="loadingModalRef" centered hide-header hide-footer>
      <div>
        <center>
          <p>Loading Results, please wait</p>
          <fingerprint-spinner :animation-duration="1500" :size="300" color="#293a93" lass="my-3" />
        </center>
      </div>
    </b-modal>
  </section>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { FingerprintSpinner } from 'epic-spinners';
import NiceDate from '../utility/NiceDate'
import FishbowlData from './FishbowlData';
import CalculatedData from './CalculatedData';
import LowestPartsData from './LowestPartsData';
import FishionService from '../../services/fishion';
import throttledQueue from 'throttled-queue';

export default {
  components: {
    FingerprintSpinner,
    NiceDate,
    FishbowlData,
    CalculatedData,
    LowestPartsData,
  },
  mounted() {
    if (Object.keys(this.$route.query).length > 0) {
      Object.keys(this.$route.query).forEach((index, key) => {
        if (Object.prototype.hasOwnProperty(this, key)) {
          this[key] = this.$route.query[key];
        }
      });
      this.searchForItem();
    }
    if (this.showErrors) {
      this.searchFishionForErrors();
    } else if (
      this.search.partNum || this.search.partDesc || this.search.productNum || this.search.itemType) {
        this.$refs.loadingModalRef.show();
        this.trimForm();
        this.searchFishion(this.search);
    }
  },
  computed: {
    ...mapGetters({
      fishionInventoryCalcs: 'fishionInventoryCalcs',
    }),
  },
  watch: {
    fishionInventoryCalcs() {
      this.$refs.loadingModalRef.hide();
    },
  },
  data() {
    return {
      infoModalText: '',
      columns: [
        { key: 'part_num', sortable: true },
        { key: 'description', sortable: true },
        { key: 'web_qty', sortable: true },
        { key: 'purchaseOrders', sortable: true },
        { key: 'how_order', label: 'Ordering Process', sortable: true },
        { key: 'adjustments', sortable: true },
        'actions'
      ],
      partDescriptions: {},
      partUOMs: {},
      search: {
        partNum: this.$route.query.partNum,
        partDesc: this.$route.query.partDesc,
        productNum: this.$route.query.productNum,
        productDescription: this.$route.query.productDescription,
        itemType: this.$route.query.itemType,
      },
      queue: throttledQueue(10, 500),
    };
  },
  methods: {
    ...mapActions({
      searchFishion: 'searchFishion',
      searchFishionForErrors: 'searchFishionForErrors',
    }),
    grabDescriptions(item) {
      this.grabDescription(item.part_num);
      let parts = item.lowest_parts.split(';');
      parts.forEach(part => {
        let num = part.split(',')[0].replace(/\*/g, '');
        if(!Object.keys(this.partDescriptions).includes(num)) {
          this.grabDescription(num)
        }
      });
    },
    grabDescription(num) {
      this.queue(() => {
        FishionService.searchInventoryCalculations({partNum: num}).then(data => {
          let pd = data.filter(e => e.part_num === num)[0];
          if(pd) {
            this.$set(this.partDescriptions, num, `${pd.part_desc} ${pd.custom_field_four.length > 0 ? '/' : ''} ${pd.custom_field_four}`);
            this.$set(this.partUOMs, num, pd.uom.trim());
          }
        })
      });
    },
    trimForm() {
      this.search.partNum = this.search.partNum == undefined ? "" : this.search.partNum.trim();
      this.search.partDesc = this.search.partDesc == undefined ? "" : this.search.partDesc.trim();
      this.search.productNum = this.search.productNum == undefined ? "" : this.search.productNum.trim();
      this.search.productDescription = this.search.productDescription == undefined ? "" : this.search.productDescription.trim();
    },
    searchForItem(evt) {
      if (evt != undefined) evt.preventDefault();
      this.$refs.loadingModalRef.show();

      this.trimForm();
      this.searchFishion(this.search);
      this.$router.push({ name: 'Qty Calculations', query: this.search})
    },
    searchForErrors(evt) {
      evt.preventDefault();
      this.$refs.loadingModalRef.show();

      this.search.partNum = "";
      this.search.partDesc = "";
      this.search.productNum = "";
      this.search.productDescription = "";

      this.searchFishionForErrors();
      this.$router.push({ name: 'Qty Calculations', query: { showErrors: 'true' }})
    },
    getItemNum(item) {
      if (item.product_num == '0') return item.part_num;
      else if (item.product_num.toUpperCase() == item.part_num.toUpperCase()) return item.part_num;
      else return item.product_num;
    },
    getQtyColor(qty) {
      if (qty < 0) return 'color: red;';
      else if (qty < 10) return 'color: darkOrange;';
      else if (qty < 40) return 'color: gold;';
      else if (qty < 100) return 'color: black;';
      else return 'color: green;';
    },
    showInfoModal(context, data) {
      if (context ==='fishbowl') {
        this.infoModalText =
          `<strong>On Hand:</strong> The total amount in stock<br />
          <strong>Allocated:</strong> Unfulfilled SO/PO/MO/TO items leaving the system<br />
          <strong>Not Sellable:</strong> The uncommitted quantity of items in locations marked 'Not Sellable'<br />
          <strong>Committed:</strong> Items picked for shipping or work orders<br />
          <strong>Not Pickable:</strong> The uncommitted quantity of items in locations marked 'Not Pickable'<br />
          <strong>On Order:</strong> Unfulfilled SO/PO/MO/TO items entering the system<br />
          <strong>Short:</strong> Quantity needed to fulfill demand<br />`;
      } else if (context === 'calculated') {
        this.infoModalText =
          `<strong>Available For Sale:</strong> The amount on hand, minus what's been allocated or in non-sellable locations<br />
          <strong>Can Build In MOs:</strong> The amount we can build, up to a maximum of the number needed to fill all MOs.<br />
          <strong>Can Build/Make:</strong> The amount we can build of this part, using inventory of its childen - doesn't include 'Can Build In MOs'<br />
          <strong>Web Inventory Adjustment:</strong> The manual adjustment of the website quantity of this part<br />
          <strong>Qty in SO Estimates:</strong> The amount of this part that is in SO estimates<br />
          <strong>Qty Needed to Build Parents:</strong> The amount reserved for building items that require this part that are in SO Estimates<br />
          <strong>Qty Not Sellable:</strong> The amount in non-sellable locations<br />`;
      } else if (context === 'lowestParts') {
        this.infoModalText =
          `<strong>Part Num:</strong> The num of the part, followed by a number of asterisks that represent how many layers deep it is<br />
          <strong>On Shelf:</strong> The amount of this item we have on shelf here at AndyMark<br/>
          <strong>Quantity To Build 1:</strong> The amount of this item we need to build 1 of the item you're inspecting<br />
          <strong>Artificial Quantity:</strong> The amount we have on shelf, "weighted" by the amount we have of its parents<br/><br />

          Below is the whole list, where each item is formatted as follows:<br/> <em>'PartNum, OnShelf, QuantityToBuild1, ArtificialQuantity'</em>:<br/>
          ${data}`;
      } else if (context == 'errors') {
        this.infoModalText =
          `<strong>Inventory Calc Errors and You</strong><br />
          <strong>Errors:</strong>
          <ul>
            <li><strong>BomInactiveErr:</strong> InventoryCalc had trouble building this BOM for some reason. Generally this is because one of the items in the BOM is inactive.</li>
            <li><strong>KitInactiveErr:</strong> See <em>BomInactiveErr</em>, but for Kits.</li>
            <li><strong>BomNotFoundErr:</strong> The item has a default BOM, but InventoryCalc couldn't find it. Generally this is because the BOM is marked inactive in Fishbowl</li>
            <li><strong>DefIdErr:</strong> This part has a matching product but no default product is set.</li>
          </ul>
          <strong>Warnings:</strong>
          <ul>
            <li><strong>NoBomToNotUse:</strong> The 'Do Not Use BOM' checkbox is checked on the part, but there is no BOM assigned to the part. Not technically an issue, but still not a bad idea to fix it.</li>
            <li><strong>BomRecWarn:</strong> For whatever reason, this item has a BOM that requires itself. Not a BOM that has a BOM that requires itself, this item literally requires itself to build itself. Yes. This has happened before.</li>
          </ul>
          Are you seeing an item with an error that isn't in this box? Put something in the #support-it channel in Slack, and an IT person will be with you momentarily.`;
      } else {
        this.infoModalText = "Something just broke, we aren't sure what to show you.";
        console.warn("Unknown Modal Context:", context);
      }
      this.$refs.infoModalRef.show();
    }
  },
}
</script>

<style scoped>
.small {
  font-size: small;
}
.em {
  font-style: italic;
}
.strong {
  font-weight: bold;
}
</style>



// WEBPACK FOOTER //
// src/components/inventory/Inventory.vue